import React, { createContext, useContext, useReducer, useEffect } from 'react';

const STORAGE_KEY = 'distorted-eclipse-state';

const initialState = {
  currentPage: 'landing',
  currentQuestion: 0,
  answers: [],
  scores: {},
};

// 从 localStorage 加载状态
function loadState() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      // 恢复答题数据和页面状态
      if (parsed.answers && parsed.answers.length === 10) {
        // 如果保存的页面是首页，就回到首页
        if (parsed.currentPage === 'landing') {
          return {
            ...initialState,
            answers: parsed.answers,
            scores: parsed.scores,
            currentQuestion: 10,
            currentPage: 'landing',
          };
        }
        // 已完成答题，恢复到之前的页面（如果是有效页面）
        const validPages = ['result', 'share', 'radarPage', 'oracle', 'drawing', 'explore'];
        const restoredPage = validPages.includes(parsed.currentPage) ? parsed.currentPage : 'result';
        return {
          ...initialState,
          answers: parsed.answers,
          scores: parsed.scores,
          currentQuestion: 10,
          currentPage: restoredPage,
        };
      } else if (parsed.answers && parsed.answers.length > 0) {
        // 答题中途，恢复到答题页面
        return {
          ...initialState,
          answers: parsed.answers,
          scores: parsed.scores,
          currentQuestion: parsed.answers.length,
          currentPage: 'quiz',
        };
      }
    }
  } catch (e) {
    console.error('Failed to load state:', e);
  }
  return initialState;
}

// 保存状态到 localStorage
function saveState(state) {
  try {
    // 首页不保存，这样在首页刷新还是首页
    if (state.answers.length > 0 && state.currentPage !== 'landing') {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        answers: state.answers,
        scores: state.scores,
        currentPage: state.currentPage,
      }));
    }
  } catch (e) {
    console.error('Failed to save state:', e);
  }
}

function reducer(state, action) {
  switch (action.type) {
    case 'SET_PAGE':
      // 如果进入 quiz 页面且已经答完题，重置答题状态
      if (action.payload === 'quiz' && state.currentQuestion >= 10) {
        localStorage.removeItem(STORAGE_KEY);
        return { ...initialState, currentPage: 'quiz' };
      }
      // 如果回到首页，更新 localStorage 中的页面状态为 landing
      if (action.payload === 'landing' && state.answers.length > 0) {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
          const parsed = JSON.parse(saved);
          parsed.currentPage = 'landing';
          localStorage.setItem(STORAGE_KEY, JSON.stringify(parsed));
        }
      }
      return { ...state, currentPage: action.payload };
    case 'ANSWER_QUESTION':
      const newAnswers = [...state.answers, action.payload];
      const newScores = { ...state.scores };
      const { archetype, weight } = action.payload;
      newScores[archetype] = (newScores[archetype] || 0) + weight;
      return {
        ...state,
        answers: newAnswers,
        scores: newScores,
        currentQuestion: state.currentQuestion + 1,
      };
    case 'RESET':
      localStorage.removeItem(STORAGE_KEY);
      return initialState;
    default:
      return state;
  }
}

const AppContext = createContext();

export function AppProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, null, loadState);
  
  // 状态变化时保存
  useEffect(() => {
    saveState(state);
  }, [state.answers, state.scores, state.currentPage]);
  
  return (
    <AppContext.Provider value={{ state, dispatch }}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const context = useContext(AppContext);
  if (!context) throw new Error('useApp must be used within AppProvider');
  return context;
}
